export type ButtonSizeEntry = {
  desktop: string;
  mobile: string;
};

export type ButtonSizes = {
  launcher: ButtonSizeEntry;
  call: ButtonSizeEntry;
  whatsapp: ButtonSizeEntry;
  wechat: ButtonSizeEntry;
};

// Fixed, frontend-owned. The backend never sends these. The icon is always
// rendered as size * ratio, so it scales to fit whatever button size the
// backend sends and can never overflow. Change here if design changes.
const ICON_RATIO: Record<keyof ButtonSizes, number> = {
  launcher: 0.75,
  call: 0.5,
  whatsapp: 0.5,
  wechat: 0.5,
};

const LAUNCHER_BORDER_RADIUS = "50%";

// Fallbacks used when the backend does not send widget_icon_size. Anything
// missing/invalid falls back here, so existing clients are unaffected.
export const DEFAULT_BUTTON_SIZES: ButtonSizes = {
  launcher: { desktop: "80px", mobile: "70px" },
  call: { desktop: "60px", mobile: "50px" },
  whatsapp: { desktop: "60px", mobile: "50px" },
  wechat: { desktop: "60px", mobile: "50px" },
};

// Clamp ranges by breakpoint (applied to all buttons). Any backend value
// outside these bounds is clamped so it can never break the layout.
const SIZE_CLAMP = {
  desktop: { min: 48, max: 100 },
  mobile: { min: 48, max: 90 },
};

// Backend sends numbers (px); tolerate numeric strings too. Clamped to a sane
// range; invalid/missing values fall back to the defaults.
function toPx(value: unknown, min: number, max: number, fallback: string): string {
  const n =
    typeof value === "number"
      ? value
      : typeof value === "string"
        ? parseFloat(value)
        : NaN;
  if (Number.isFinite(n)) return `${Math.min(Math.max(n, min), max)}px`;
  return fallback;
}

function resolveEntry(raw: any, fallback: ButtonSizeEntry): ButtonSizeEntry {
  return {
    desktop: toPx(
      raw?.desktop,
      SIZE_CLAMP.desktop.min,
      SIZE_CLAMP.desktop.max,
      fallback.desktop,
    ),
    mobile: toPx(
      raw?.mobile,
      SIZE_CLAMP.mobile.min,
      SIZE_CLAMP.mobile.max,
      fallback.mobile,
    ),
  };
}

export function resolveButtonSizes(clientConfig: any): ButtonSizes {
  const launcherRaw = clientConfig?.widget_positioning?.widget_icon_size;
  const commCfg = clientConfig?.communication_redirects_config ?? {};
  return {
    launcher: resolveEntry(launcherRaw, DEFAULT_BUTTON_SIZES.launcher),
    call: resolveEntry(commCfg.call?.widget_icon_size, DEFAULT_BUTTON_SIZES.call),
    whatsapp: resolveEntry(
      commCfg.whatsapp?.widget_icon_size,
      DEFAULT_BUTTON_SIZES.whatsapp,
    ),
    wechat: resolveEntry(
      commCfg.wechat?.widget_icon_size,
      DEFAULT_BUTTON_SIZES.wechat,
    ),
  };
}

export function launcherSizeVars(entry: ButtonSizeEntry): string {
  return [
    `--vf-launcher-size-d:${entry.desktop}`,
    `--vf-launcher-size-m:${entry.mobile}`,
    `--vf-launcher-icon-ratio:${ICON_RATIO.launcher}`,
    `--vf-launcher-radius:${LAUNCHER_BORDER_RADIUS}`,
  ].join(";");
}

export function commSizeVars(
  type: "call" | "whatsapp" | "wechat",
  entry: ButtonSizeEntry,
): string {
  return [
    `--vf-comm-size-d:${entry.desktop}`,
    `--vf-comm-size-m:${entry.mobile}`,
    `--vf-comm-icon-ratio:${ICON_RATIO[type]}`,
  ].join(";");
}
